import React from "react";
import { useTranslation } from "react-i18next";
import "./Pagination.css";

export interface PaginationProps {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Called when page changes */
  onPageChange: (page: number) => void;
  /** Whether there are more items after current page */
  hasMore: boolean;
  /** Number of items per page (used for display) */
  itemsPerPage?: number;
  /** Total number of items (optional, for "Showing X of Y") */
  totalItems?: number;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Reusable pagination component with Previous/Next navigation.
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  onPageChange,
  hasMore,
  itemsPerPage = 20,
  totalItems,
  className = "",
}) => {
  const { t } = useTranslation("common");

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (hasMore) {
      onPageChange(currentPage + 1);
    }
  };

  // Calculate display info if totalItems is provided
  const showingInfo =
    totalItems !== undefined ? (
      <span className="pagination-showing">
        {t("table.showing")} {(currentPage - 1) * itemsPerPage + 1}-
        {Math.min(currentPage * itemsPerPage, totalItems)} {t("table.of")}{" "}
        {totalItems}
      </span>
    ) : null;

  return (
    <div className={`pagination ${className}`}>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handlePrevious}
        disabled={currentPage === 1}
      >
        {t("actions.back")}
      </button>

      <span className="page-info">
        {t("table.page")} {currentPage}
        {showingInfo}
      </span>

      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleNext}
        disabled={!hasMore}
      >
        {t("actions.next")}
      </button>
    </div>
  );
};

export default Pagination;
